import React, { useMemo } from 'react';
import { Mafs, Coordinates, Polygon, Text, Theme } from 'mafs';

export const PolygonExplorer = ({ config, variables }) => {
    // 1. Resolve Vertices from Variables
    const points = useMemo(() => {
        const rawVerts = config.vertices || [[0, 0], [2, 0], [1, 2]];
        return rawVerts.map(v => [
            Number(typeof v[0] === 'string' ? (variables[v[0]] ?? 0) : v[0]),
            Number(typeof v[1] === 'string' ? (variables[v[1]] ?? 0) : v[1])
        ]);
    }, [config.vertices, variables]);

    // 2. Auto-Scale ViewBox
    const viewBox = useMemo(() => {
        if (points.length === 0) return { x: [-5, 5], y: [-5, 5] };

        const xs = points.map(p => p[0]);
        const ys = points.map(p => p[1]);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const paddingX = Math.max(1, (maxX - minX) * 0.2);
        const paddingY = Math.max(1, (maxY - minY) * 0.2);

        return {
            x: [minX - paddingX, maxX + paddingX],
            y: [minY - paddingY, maxY + paddingY]
        };
    }, [points]);

    // 3. True Centroid Calculation (Geometric Center)
    const { centerX, centerY, area } = useMemo(() => {
        if (points.length < 3) return { centerX: 0, centerY: 0, area: 0 };

        let doubleArea = 0;
        let cx = 0;
        let cy = 0;
        const n = points.length;

        for (let i = 0; i < n; i++) {
            const [x0, y0] = points[i];
            const [x1, y1] = points[(i + 1) % n];
            const cross = x0 * y1 - x1 * y0;

            doubleArea += cross;
            cx += (x0 + x1) * cross;
            cy += (y0 + y1) * cross;
        }

        const a = doubleArea / 2;
        // Avoid division by zero for degenerate polygons
        if (Math.abs(a) < 1e-6) return { centerX: points[0][0], centerY: points[0][1], area: 0 };

        return {
            centerX: cx / (6 * a),
            centerY: cy / (6 * a),
            area: Math.abs(a)
        };
    }, [points]);

    return (
        <Mafs viewBox={viewBox} pan={false}>
            <Coordinates.Cartesian />

            <Polygon points={points} color={config.color || Theme.orange} strokeWidth={3} />

            {/* Vertex Labels (Precision fixed to 1 decimal) */}
            {config.labels?.vertices && points.map((p, i) => (
                <Text key={i} x={p[0]} y={p[1]} attach="nw" size={16}>
                    {`(${p[0] % 1 === 0 ? p[0] : p[0].toFixed(1)}, ${p[1] % 1 === 0 ? p[1] : p[1].toFixed(1)})`}
                </Text>
            ))}

            {/* Centroid Label */}
            {config.labels?.area && (
                <Text x={centerX} y={centerY} size={20} weight="bold">
                    Area: {Number(area.toFixed(2))}
                </Text>
            )}
        </Mafs>
    );
};